import api from './api';
import { PaginatedResponse, ApiResponse } from '../types';

export interface FormFeed {
  id: number;
  form_id: number;
  name: string;
  target_module: string;
  primary_key: string;
  field_mappings: Record<string, string>;
  on_submit: boolean;
  create_or_update: 'create' | 'update' | 'create_or_update';
  do_not_update_if_exists: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  form?: {
    id: number;
    title: string;
  };
}

export interface FormOption {
  id: number;
  title: string;
}

export interface ProjectRequestField {
  key: string;
  label: string;
  type: string;
}

export interface MappingOptions {
  form_fields: Array<{
    name: string;
    label: string;
    type: string;
  }>;
  project_request_fields: ProjectRequestField[];
}

export const formFeedsApi = {
  list: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<FormFeed>> => {
    const { data } = await api.get<PaginatedResponse<FormFeed>>('/form-feeds', { params });
    return data;
  },

  getById: async (id: number): Promise<FormFeed> => {
    const { data } = await api.get<FormFeed>(`/form-feeds/${id}`);
    return data;
  },

  create: async (feedData: {
    form_id: number;
    name: string;
    target_module: string;
    primary_key: string;
    field_mappings: Record<string, string>;
    on_submit?: boolean;
    create_or_update?: 'create' | 'update' | 'create_or_update';
    do_not_update_if_exists?: boolean;
    is_active?: boolean;
  }): Promise<ApiResponse<FormFeed>> => {
    const { data } = await api.post<ApiResponse<FormFeed>>('/form-feeds', feedData);
    return data;
  },

  update: async (id: number, feedData: Partial<{
    name: string;
    field_mappings: Record<string, string>;
    is_active: boolean;
    on_submit: boolean;
    create_or_update: 'create' | 'update' | 'create_or_update';
    do_not_update_if_exists: boolean;
  }>): Promise<ApiResponse<FormFeed>> => {
    const { data } = await api.put<ApiResponse<FormFeed>>(`/form-feeds/${id}`, feedData);
    return data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const { data } = await api.delete<ApiResponse<null>>(`/form-feeds/${id}`);
    return data;
  },

  getFormOptions: async (): Promise<FormOption[]> => {
    const { data } = await api.get<FormOption[]>('/form-feeds/options/forms');
    return data;
  },

  getProjectRequestFields: async (): Promise<ProjectRequestField[]> => {
    const { data } = await api.get<ProjectRequestField[]>('/form-feeds/options/project-request-fields');
    return data;
  },

  getMappingOptions: async (formId: number): Promise<MappingOptions> => {
    const { data } = await api.get<MappingOptions>('/form-feeds/mapping-options', {
      params: { form_id: formId },
    });
    return data;
  },

  getFeedFormFields: async (feedId: number): Promise<Array<{ name: string; label: string; type: string }>> => {
    const { data } = await api.get<Array<{ name: string; label: string; type: string }>>(`/form-feeds/${feedId}/form-fields`);
    return data;
  },

  syncSubmission: async (feedId: number, submissionId: number): Promise<ApiResponse<any>> => {
    const { data } = await api.post<ApiResponse<any>>(`/form-feeds/${feedId}/submissions/${submissionId}/sync`);
    return data;
  },
};
